/* Approval card (rules 27-29; spec 6.5; human-in-the-loop pattern after assistant-ui / CopilotKit / AG-UI, MIT).
   Tier 0-1: approve once (Tier 1 may allow for the rest of the task). Tier 2: approve each action,
   no "always allow", focus starts on Edit. Tier 3: money and credentials are handed to the user. */
import { useEffect, useRef, useState } from 'react';
import { CircleCheck, CircleX, Clock, Coins, Hand, ShieldCheck, Undo2 } from 'lucide-react';
import type { Approval, ApprovalDecision } from '../lib/api-types';
import { Button } from './basics';

export const TIER_LABEL: Record<number, string> = {
  0: 'Tier 0 · Read only',
  1: 'Tier 1 · Reversible, your space',
  2: 'Tier 2 · External side effect',
  3: 'Tier 3 · Money: you do it',
};

export type ApprovalUiState = 'pending' | 'edit' | ApprovalDecision;

export interface ApprovalCardProps {
  approval: Approval;
  /** Controlled state; when omitted the card manages it. */
  state?: ApprovalUiState;
  onDecision?: (decision: ApprovalDecision, extra: { allowForTask?: boolean; editedPreview?: string }) => void;
  /** Time and who approved, for the collapsed log line. */
  resolvedNote?: string;
  onUndo?: () => void;
  onAskAgain?: () => void;
  /** Overrides `approval.allowTask`: false hides "Allow for the rest of this task". */
  allowTask?: boolean;
  /** ISO time the request expires (defaults to `approval.expiresAt`); shows a countdown. */
  expiresAt?: string;
  /** ISO time until which an approved action can be undone; shows "Undo (1:52 left)". */
  undoUntil?: string;
  /** Clock for tests. */
  now?: () => number;
}

/** "12 min" / "1:52" style countdown text, or null when past. */
export function countdown(untilIso: string | undefined, now: number): string | null {
  if (!untilIso) return null;
  const ms = Date.parse(untilIso) - now;
  if (!Number.isFinite(ms) || ms <= 0) return null;
  const s = Math.ceil(ms / 1000);
  if (s >= 3600) return `${Math.floor(s / 3600)} h ${Math.floor((s % 3600) / 60)} min`;
  if (s >= 600) return `${Math.round(s / 60)} min`;
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

function useTick(active: boolean, now: () => number): number {
  const [t, setT] = useState(now);
  useEffect(() => {
    if (!active) return;
    const id = setInterval(() => setT(now()), 1000);
    return () => clearInterval(id);
  }, [active, now]);
  return t;
}

interface MoneyLine { name?: string; title?: string; qty?: number; quantity?: number; price?: number | string; total?: number | string }
type Money = { merchant?: string; items?: MoneyLine[]; subtotal?: number | string; shipping?: number | string; tax?: number | string; total?: number | string; currency?: string; shipTo?: string; paymentMethod?: string; note?: string };
const money = (v: unknown, currency = 'USD') => (typeof v === 'number' ? new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(v) : typeof v === 'string' ? v : '');

/** Exact amounts for money approvals (SHOP-02): merchant, items, subtotal, shipping, tax, total. */
function MoneyDetails({ d }: { d: Money }) {
  const c = d.currency ?? 'USD';
  const rows: [string, unknown][] = [['Subtotal', d.subtotal], ['Shipping', d.shipping], ['Tax', d.tax]];
  return (
    <div className="appr-money" data-cap="SHOP-02">
      {d.merchant && <div className="small"><b>{d.merchant}</b></div>}
      <table>
        <tbody>
          {(d.items ?? []).map((it, i) => (
            <tr key={i}><td>{(it.qty ?? it.quantity ?? 1)} × {it.name ?? it.title ?? 'Item'}</td><td style={{ textAlign: 'right' }}>{money(it.total ?? it.price, c)}</td></tr>
          ))}
          {rows.filter(([, v]) => v !== undefined && v !== null).map(([k, v]) => <tr key={k}><td className="muted">{k}</td><td style={{ textAlign: 'right' }}>{money(v, c)}</td></tr>)}
          <tr><th>Total</th><th style={{ textAlign: 'right' }}>{money(d.total, c)}</th></tr>
        </tbody>
      </table>
      {(d.shipTo || d.paymentMethod) && <div className="small muted">{[d.shipTo && `Ships to ${d.shipTo}`, d.paymentMethod && `Paid with ${d.paymentMethod}`].filter(Boolean).join(' · ')}</div>}
      {d.note && <div className="small muted">{d.note}</div>}
    </div>
  );
}
const isMoney = (v: unknown): v is Money => typeof v === 'object' && v !== null && 'total' in v;
const realNow = () => Date.now();

export function ApprovalCard({ approval: a, state: stateProp, onDecision, resolvedNote, onUndo, onAskAgain, allowTask, expiresAt, undoUntil, now = realNow }: ApprovalCardProps) {
  const [stateS, setStateS] = useState<ApprovalUiState>('pending');
  const [allow, setAllow] = useState(false);
  const [draft, setDraft] = useState(a.preview ?? '');
  // A resolved state from the server wins; while pending, the card shows the user's local choice at once.
  const state: ApprovalUiState = stateProp && stateProp !== 'pending' ? stateProp : stateS;
  const editRef = useRef<HTMLButtonElement>(null);
  const primary = a.primaryLabel ?? (a.tier === 3 ? 'Take over' : 'Approve');
  const offersTask = a.tier <= 1 && (allowTask ?? a.allowTask) !== false;
  const expiry = expiresAt ?? a.expiresAt;
  const t = useTick(!!(expiry || undoUntil), now);
  const expiresIn = state === 'pending' || state === 'edit' ? countdown(expiry, t) : null;
  const undoLeft = countdown(undoUntil, t);

  const decide = (d: ApprovalDecision) => {
    setStateS(d);
    onDecision?.(d, { allowForTask: offersTask ? allow : undefined, editedPreview: d === 'edited' ? draft : undefined });
  };

  // Tier 2-3: never default-focus the primary; put focus on Edit to prevent an accidental Enter.
  useEffect(() => { if (state === 'pending' && a.tier >= 2) editRef.current?.focus({ preventScroll: true }); }, [state, a.tier]);

  if (state === 'approved' || state === 'edited') {
    return (
      <div className="appr done" data-tier={a.tier} data-state={state} data-cap="AGENT-12" role="status">
        <CircleCheck size={18} aria-hidden="true" />
        <span><b>{primary}</b>: {state === 'edited' ? 'approved with edits' : 'approved'}{resolvedNote ? ` ${resolvedNote}` : ''}.{' '}
          {onUndo && (undoUntil ? undoLeft : a.tier === 2) && <button type="button" className="link" onClick={onUndo}>{undoLeft ? `Undo (${undoLeft} left)` : 'Undo'}</button>}</span>
      </div>
    );
  }
  if (state === 'denied' || state === 'expired') {
    return (
      <div className={`appr done ${state}`} data-tier={a.tier} data-state={state} data-cap="AGENT-12" role="status">
        <CircleX size={18} aria-hidden="true" />
        <span><b>{state === 'denied' ? 'Denied' : 'Expired'}:</b> {a.summary}. {state === 'denied' ? 'The agent is re-planning without this step.' : 'Nothing was done.'}{' '}
          {onAskAgain && <button type="button" className="link" onClick={() => { setStateS('pending'); onAskAgain(); }}>Ask again</button>}</span>
      </div>
    );
  }
  if (state === 'handoff') {
    return (
      <div className="appr handoff" data-tier={a.tier} data-state={state} data-cap="AGENT-12">
        <div className="appr-sum"><Hand size={18} aria-hidden="true" /><b>Your turn: finish this on the agent's computer</b></div>
        <div className="small">The agent has stopped. You are in control; card details and passwords never pass through the agent.</div>
        <div className="appr-act"><span className="sp" /><Button variant="primary" onClick={() => decide('approved')}>I'm done, hand back</Button></div>
      </div>
    );
  }

  const editing = state === 'edit';
  return (
    <div className="appr" data-tier={a.tier} data-state={state} role="group" aria-label={`Approval needed: ${a.summary}`} data-cap="AGENT-12">
      <div className="appr-hd">
        <span className="tier" data-tier={a.tier}>{TIER_LABEL[a.tier] ?? `Tier ${a.tier}`}</span>
        {(a.agent || a.step) && <span className="small muted">{[a.agent, a.step].filter(Boolean).join(' · ')}</span>}
      </div>
      <div className="appr-sum"><ShieldCheck size={18} aria-hidden="true" /><b>{a.summary}</b></div>
      {a.preview !== undefined && (editing
        ? <textarea className="appr-prev" aria-label="Edit before approving" value={draft} onChange={(e) => setDraft(e.target.value)} autoFocus />
        : <div className="appr-prev">{draft}</div>)}
      <div className="appr-meta">
        {a.reversibility && <span><Undo2 size={14} aria-hidden="true" /> {a.reversibility}</span>}
        {a.cost && <span><Coins size={14} aria-hidden="true" /> {a.cost}</span>}
        {expiresIn && <span className="appr-exp"><Clock size={14} aria-hidden="true" /> Expires in {expiresIn}</span>}
      </div>
      {isMoney(a.details) && <MoneyDetails d={a.details} />}
      {offersTask && (
        <label className="chk"><input type="checkbox" checked={allow} onChange={(e) => setAllow(e.target.checked)} /> Allow for the rest of this task</label>
      )}
      <div className="appr-act">
        <Button variant="danger" onClick={() => decide('denied')}>Deny</Button>
        <span className="sp" />
        {editing ? (
          <Button variant="primary" onClick={() => decide('edited')}>Approve with edits</Button>
        ) : (
          <>
            {a.preview !== undefined && a.tier < 3 && <Button ref={editRef} onClick={() => setStateS('edit')}>Edit</Button>}
            <Button variant="primary" onClick={() => (a.tier === 3 ? decide('handoff') : decide('approved'))}>{primary}</Button>
          </>
        )}
      </div>
      {a.tier >= 2 && <div className="small muted">No "always allow" at this tier.</div>}
    </div>
  );
}
